import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Brain,
  CheckCircle2,
  Clock3,
  FileText,
  HeartHandshake,
  MessageSquareWarning,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';

interface GestionPlaintesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type StepCard = {
  step: number;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
  border: string;
  bg: string;
  bullets: string[];
  example?: string;
  note?: string;
};

const STEP_CARDS: StepCard[] = [
  {
    step: 1,
    title: 'Accueillir la plainte',
    icon: MessageSquareWarning,
    gradient: 'from-rose-500 to-orange-500',
    border: 'border-rose-200',
    bg: 'bg-rose-50',
    bullets: [
      'Accueillir la plainte avec calme et professionnalisme.',
      'Écouter attentivement le client sans l’interrompre pour bien comprendre ses préoccupations.',
    ],
  },
  {
    step: 2,
    title: 'Reformuler la plainte',
    icon: Users,
    gradient: 'from-amber-500 to-yellow-500',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    bullets: [
      'Reformuler la plainte pour s’assurer d’avoir bien compris l’insatisfaction du client.',
    ],
    example:
      '« Si je comprends bien, vous avez rencontré un problème avec notre logiciel après la mise à jour? »',
  },
  {
    step: 3,
    title: 'S’excuser, montrer de l’empathie et baisser l’émotion',
    icon: HeartHandshake,
    gradient: 'from-pink-500 to-rose-500',
    border: 'border-pink-200',
    bg: 'bg-pink-50',
    bullets: [
      'Présenter des excuses sincères et montrer de l’empathie (mais pas trop) pour la situation du client.',
      'S’assurer de descendre l’émotion négative vécue par le client.',
      'Ne pas embarquer contre le technicien ou l’équipe; mettre l’emphase sur l’amélioration des procédures.',
    ],
    example:
      '« Je suis désolé pour les désagréments que cela a pu causer. Je comprends à quel point cela peut être frustrant. Permettez-moi d’aller valider tous les faits pour être certain de bien comprendre ce qui s’est passé et voir comment on peut régler le tout. »',
    note:
      'Le but ici n’est pas de défendre BZ trop vite. Le but est de faire baisser la tension pour revenir à un échange constructif.',
  },
  {
    step: 4,
    title: 'Proposer une solution',
    icon: ShieldCheck,
    gradient: 'from-emerald-500 to-green-500',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50',
    bullets: [
      'Proposer une action corrective pour résoudre le problème.',
      'S’assurer que la solution est réalisable et satisfaisante pour le client.',
    ],
    example:
      '« Je vais vérifier avec l’équipe technique s’il y a un logiciel X qui pourra éviter ce type de demande. »',
  },
  {
    step: 5,
    title: 'Fixer un délai',
    icon: Clock3,
    gradient: 'from-sky-500 to-blue-500',
    border: 'border-sky-200',
    bg: 'bg-sky-50',
    bullets: [
      'Fixer un délai pour revenir au client ainsi qu’un délai de résolution du problème.',
      'Se donner une marge de manœuvre raisonnable.',
    ],
    example:
      '« Laissez-moi 2-3 journées pour consulter tous les intervenants et je vous reviens avec une solution que nous pourrons mettre en place dans les prochaines semaines. »',
  },
  {
    step: 6,
    title: 'Analyser et améliorer',
    icon: Search,
    gradient: 'from-indigo-500 to-violet-500',
    border: 'border-indigo-200',
    bg: 'bg-indigo-50',
    bullets: [
      'Aller vérifier les dires du client.',
      'Consulter les billets.',
      'Consulter le prime technique.',
      'Consulter le directeur du département en question.',
    ],
  },
  {
    step: 7,
    title: 'Suivre et informer le client',
    icon: CheckCircle2,
    gradient: 'from-teal-500 to-cyan-500',
    border: 'border-teal-200',
    bg: 'bg-teal-50',
    bullets: [
      'Contacter le client et lui expliquer en détail ce qui s’est passé et comment BZ va corriger le tir.',
      'S’assurer que la solution a été mise en œuvre et informer le client des actions prises.',
      'Demander au client s’il est satisfait du résultat et s’il a d’autres préoccupations.',
      'Assurer des suivis avec l’équipe technique ainsi que le client.',
    ],
  },
  {
    step: 8,
    title: 'Documenter la plainte',
    icon: FileText,
    gradient: 'from-slate-600 to-slate-800',
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    bullets: [
      'Garder en note la plainte du client.',
      'Au besoin, la réadresser lors du plan directeur ou d’un appel spontané plusieurs mois plus tard.',
    ],
    note:
      'Une plainte bien documentée devient une matière première d’amélioration continue, pas juste un incident classé.',
  },
];

export default function GestionPlaintesModal({ isOpen, onClose }: GestionPlaintesModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/55 z-40"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-y-auto">
              <div className="sticky top-0 bg-bz-dark text-white p-6 rounded-t-2xl z-10">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h2 className="text-3xl font-bold">Gestion de plaintes</h2>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20">
                        <Brain className="w-4 h-4 text-purple-200" />
                        <span className="text-xs text-purple-100 font-semibold">AI</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-bz-blue text-white text-sm font-medium rounded-full">
                        Excellence et Compétences
                      </span>
                      <span className="px-3 py-1 bg-white/10 text-white text-sm rounded-full border border-white/15">
                        Processus en 8 étapes
                      </span>
                    </div>
                    <p className="text-white/85 max-w-4xl leading-relaxed">
                      Une plainte bien gérée ne protège pas seulement la relation client — elle démontre notre maturité,
                      notre rigueur et notre capacité à transformer un irritant en amélioration concrète.
                    </p>
                  </div>

                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 md:p-8 space-y-8">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-lg shrink-0">
                      <Sparkles className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">L’objectif réel</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Le but n’est pas juste de fermer une plainte. Le but, c’est de calmer la situation,
                        comprendre les faits, corriger le tir, rassurer le client et améliorer nos pratiques pour ne pas revivre la même chose.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {STEP_CARDS.map((card, index) => {
                    const Icon = card.icon;
                    return (
                      <motion.div
                        key={card.step}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className={`rounded-2xl border ${card.border} ${card.bg} shadow-sm overflow-hidden`}
                      >
                        <div className={`bg-gradient-to-r ${card.gradient} px-5 py-4 text-white`}>
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <p className="text-white/80 text-xs font-semibold uppercase tracking-[0.18em]">Étape {card.step}</p>
                              <h3 className="text-xl font-bold leading-tight">{card.title}</h3>
                            </div>
                          </div>
                        </div>

                        <div className="p-5 space-y-4">
                          <ul className="space-y-2.5">
                            {card.bullets.map((bullet) => (
                              <li key={bullet} className="flex items-start gap-3 text-gray-700 leading-relaxed">
                                <ArrowRight className="w-4 h-4 mt-1 text-gray-500 shrink-0" />
                                <span>{bullet}</span>
                              </li>
                            ))}
                          </ul>

                          {card.example && (
                            <div className="rounded-xl border border-white/70 bg-white/80 p-4">
                              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">Exemple</p>
                              <p className="text-gray-700 italic leading-relaxed">{card.example}</p>
                            </div>
                          )}

                          {card.note && (
                            <div className="rounded-xl border border-gray-200 bg-white/70 p-4">
                              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">Point important</p>
                              <p className="text-gray-700 leading-relaxed">{card.note}</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="rounded-2xl border border-blue-200 bg-blue-50 p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-lg">
                        <Target className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Bonnes pratiques clés</h3>
                    </div>
                    <div className="space-y-3 text-gray-700">
                      <div className="rounded-xl bg-white/80 border border-blue-100 p-4">Rester calme, même si le client est chargé émotionnellement.</div>
                      <div className="rounded-xl bg-white/80 border border-blue-100 p-4">Valider les faits avant de conclure ou de prendre parti.</div>
                      <div className="rounded-xl bg-white/80 border border-blue-100 p-4">Toujours revenir avec une suite claire: action, délai et responsable.</div>
                      <div className="rounded-xl bg-white/80 border border-blue-100 p-4">Transformer chaque plainte en levier d’amélioration continue.</div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-lg">
                        <AlertTriangle className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Ce qu’il faut éviter</h3>
                    </div>
                    <div className="space-y-3 text-gray-700">
                      <div className="rounded-xl bg-white/80 border border-emerald-100 p-4">Se justifier trop vite ou contredire le client dès le début.</div>
                      <div className="rounded-xl bg-white/80 border border-emerald-100 p-4">Blâmer un technicien, une équipe ou un collègue devant le client.</div>
                      <div className="rounded-xl bg-white/80 border border-emerald-100 p-4">Promettre un délai irréaliste juste pour calmer la situation.</div>
                      <div className="rounded-xl bg-white/80 border border-emerald-100 p-4">Fermer le dossier sans suivi réel ni apprentissage concret.</div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
